import { Box, Button, styled, Theme, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { getCenterStyles, getFluidBoxStyles, getScreenFluidBoxStyles } from "../theme/mixins";
import { v1 } from "uuid";
import { isDesktop } from "../utils/navigator";

export type CameraProps = {
    documentRatio: [number, number];
    headlineText: string;
    descriptionText: string;
    onChange: (image: File) => void;
}

export const Camera = (props: CameraProps) => {
    const theme = useTheme();

    const { black, white } = theme.palette.common;

    const videoRef = useRef(null);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const [_videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);

    const [selectedDeviceId, setSelectedDeviceId] = useState('');

    const [error, setError] = useState<boolean>(false);

    const [errors, setErrors] = useState<string[]>([]);

    const isDesktopDevice = isDesktop();

    if (isDesktopDevice) return <CameraNotAvailable onChange={props.onChange} />

    const captureImage = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const context = canvas.getContext('2d');
            if (context) {
                // Set canvas size to match video size
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                // Draw the video frame onto the canvas
                context.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Convert canvat to File
                const file = new File([canvas.toDataURL('image/png')], "image.png", { type: "image/png" });
                props.onChange(file);
            }
        }
    };

    useEffect(() => {
        // Fetch and list all video input devices
        const getDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputs = devices.filter(device => device.kind === 'videoinput');
                setVideoDevices(videoInputs);
                if (videoInputs.length > 0) {
                    const backCamera = videoInputs.find(device => device.label.toLowerCase().includes('back'));
                    setSelectedDeviceId(backCamera ? backCamera.deviceId : null);
                }
            } catch (error) {
                setErrors([...errors, error.message]);
                setError(true);
            }
        };
        getDevices();
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            const constraints = {
                video: {
                    deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
                },
            };
            navigator.mediaDevices.getUserMedia(constraints)
                .then((stream) => {
                    if (video) video.srcObject = stream;
                })
                .catch((error) => {
                    setErrors([...errors, error.message]);
                    setError(true);
                });
        }
    }, [selectedDeviceId]);

    if (error) return <CameraNotAvailable onChange={props.onChange} />

    return <Box sx={{
        width: "100vw",
        height: "100%",
        backgroundColor: black,
        display: "flex",
        flexDirection: "column",
        padding: 2,
        gap: 2,
    }}>
        <Typography variant="h5" color={white}>{props.headlineText}</Typography>

        <StyledCameraContainer>
            <video
                autoPlay
                playsInline
                ref={videoRef}
                style={{ ...getFluidBoxStyles(), objectFit: "cover", backgroundColor: "black" }}
            ></video>
        </StyledCameraContainer>

        <Typography variant="h5" width="100%" color={white} textAlign="center">{props.descriptionText}</Typography>

        <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
        ></canvas>

        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "flex-end", paddingBottom: 3, paddingTop: 1 }}>
            <StyledCameraButton onClick={captureImage} />
        </Box>
    </Box>
}

export type CameraNotAvailableProps = {
    onChange: CameraProps["onChange"];
}

const CameraNotAvailable = (props: CameraNotAvailableProps) => {
    const id = v1();

    return <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles("column"), padding: 2, gap: 2 }}>
        <Typography variant="body2" textAlign="center">
            Chyba pri pokuse o získanie prístupu ku kamere.
        </Typography>

        <input
            id={id}
            type="file"
            hidden
            accept="image/*"
            onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) props.onChange(file);
            }}
        />

        <label htmlFor={id}>
            <Button size="small" color="primary" component="span">
                Nahrať fotku
            </Button>
        </label>
    </Box>
}

export const StyledCameraContainer = styled('div', {})(({ theme }: { theme: Theme }) => ({
    width: "100%",
    borderRadius: `${theme.shape.borderRadius * 2}px`,
    backgroundColor: theme.palette.grey[800],
    height: 260,
    overflow: "hidden",
}));

export type StyledCameraButtonProps = {
    onClick: () => void;
}

export const StyledCameraButton = (props: StyledCameraButtonProps) => {
    const theme = useTheme();

    const { black, white } = theme.palette.common;

    const getStyles = (size: number, color: string) => ({
        width: size,
        height: size,
        borderRadius: "100%",
        backgroundColor: color,
        ...getCenterStyles(),
    })

    return <Box onClick={props.onClick} sx={getStyles(75, white)}>
        <Box sx={getStyles(65, black)}>
            <Box sx={getStyles(60, white)}></Box>
        </Box>
    </Box>
}